import config from "../config";

type Variant = "new" | "legacy" | "unresolved";
type Outcome =
  "ready" | "error" | "abandoned" | "timeout" | "hidden" | "legacy";
type Phase =
  | "router_ready_ms"
  | "bootstrap_ready_ms"
  | "dependencies_ready_ms"
  | "grid_settled_ms";
type Entry = {
  started: number;
  entry_type:
    | "initial_load"
    | "dashboard_navigation"
    | "calendar_switch"
    | "other_navigation";
  requested_variant: Variant;
  variant: Variant;
  redirected: boolean;
  new_module_cached_at_entry: boolean;
  timings: Partial<Record<Phase, number>> & Record<string, number>;
  timer: ReturnType<typeof setTimeout>;
};
type Measurement = Record<string, string | number | boolean>;
type Router = {
  state: { location: { pathname: string } };
  subscribe: (
    callback: (state: { location: { pathname: string } }) => void
  ) => () => void;
};

const NEW_PATH = "/dashboard/calendar-new";
const LEGACY_PATH = "/dashboard/calendar";
const TIMEOUT_MS = 120_000;
let active: Entry | undefined;
let previousPath: string | undefined;
let bootstrapReady = false;
let reporter: ((measurement: Measurement) => void) | undefined;
const pending: Measurement[] = [];
const loadedModules = new Set<"new" | "legacy">();
const elapsed = (entry: Entry) =>
  Math.round((performance.now() - entry.started) * 10) / 10;
const variantFor = (path: string): Variant =>
  path === NEW_PATH ? "new" : path === LEGACY_PATH ? "legacy" : "unresolved";

function send(measurement: Measurement) {
  // Observability loads asynchronously. Keep a bounded buffer without importing
  // its SDK onto the startup path. Telemetry must never break navigation.
  if (!reporter) {
    if (pending.length === 10) pending.shift();
    pending.push(measurement);
    return;
  }
  try {
    reporter(measurement);
  } catch {
    // Best effort; no retries or recursive telemetry on the critical path.
  }
}

function finish(entry: Entry, outcome: Outcome, details: Measurement = {}) {
  if (active !== entry) return;
  active = undefined;
  clearTimeout(entry.timer);
  send({
    schema_version: 1,
    entry_type: entry.entry_type,
    requested_variant: entry.requested_variant,
    variant: entry.variant,
    redirected: entry.redirected,
    new_module_cached_at_entry: entry.new_module_cached_at_entry,
    outcome,
    started_at_ms: Math.round(performance.timeOrigin + entry.started),
    duration_ms: elapsed(entry),
    ...entry.timings,
    ...details,
  });
}

export function registerCalendarEntryReporter(
  next: (measurement: Measurement) => void
) {
  reporter = next;
  pending.splice(0).forEach((measurement) => send(measurement));
}

export function markCalendarEntryPhase(phase: Phase) {
  if (phase === "bootstrap_ready_ms") bootstrapReady = true;
  if (active && active.timings[phase] === undefined) {
    active.timings[phase] = elapsed(active);
  }
}

export function failCalendarEntry(
  stage: "api_setup" | "user_bootstrap" | "route_render"
) {
  if (active) finish(active, "error", { failure_stage: stage });
}

export function hasPendingCalendarEntry() {
  return active?.variant === "new";
}

/** Observe committed router locations before React renders a lazy route. */
export function trackCalendarEntries(router: Router) {
  if (!config.observabilityEnabled) return () => {};
  const observe = ({ location }: Router["state"]) => {
    const path = location.pathname.replace(/\/$/, "");
    if (path === previousPath) return;
    const from = previousPath;
    previousPath = path;
    if (
      active?.redirected &&
      active.variant === variantFor(path) &&
      ((from === LEGACY_PATH && path === NEW_PATH) ||
        (from === NEW_PATH && path === LEGACY_PATH))
    ) {
      return;
    }
    if (active) finish(active, "abandoned");
    if (variantFor(path) === "unresolved") return;
    const started = from === undefined ? 0 : performance.now();
    const entry: Entry = {
      started,
      entry_type:
        from === undefined
          ? "initial_load"
          : from === "/dashboard"
            ? "dashboard_navigation"
            : variantFor(from) === "unresolved"
              ? "other_navigation"
              : "calendar_switch",
      requested_variant: variantFor(path),
      variant: path === NEW_PATH ? "new" : "unresolved",
      redirected: false,
      new_module_cached_at_entry: loadedModules.has("new"),
      timings: {},
      timer: setTimeout(
        () => finish(entry, "timeout"),
        Math.max(0, TIMEOUT_MS - (performance.now() - started))
      ),
    };
    active = entry;
    markCalendarEntryPhase("router_ready_ms");
    if (bootstrapReady) entry.timings.bootstrap_ready_ms = 0;
    if (document.visibilityState === "hidden") finish(entry, "hidden");
  };
  observe(router.state);
  const unsubscribe = router.subscribe(observe);
  const onHidden = () => {
    if (active && document.visibilityState === "hidden")
      finish(active, "hidden");
  };
  const onPageHide = () => {
    if (active) finish(active, "abandoned");
  };
  document.addEventListener("visibilitychange", onHidden);
  window.addEventListener("pagehide", onPageHide);
  return () => {
    unsubscribe();
    document.removeEventListener("visibilitychange", onHidden);
    window.removeEventListener("pagehide", onPageHide);
    if (active) clearTimeout(active.timer);
    active = undefined;
    previousPath = undefined;
  };
}

export function resolveCalendarEntry(
  resolution: "legacy" | "redirect",
  redirectVariant: "new" | "legacy" = "new"
) {
  if (!active) return;
  if (resolution === "redirect") {
    active.redirected = true;
    active.variant = redirectVariant;
  } else {
    active.variant = "legacy";
    // Legacy has a different loading contract. Do not call its mount "ready".
    finish(active, "legacy");
  }
}

export async function measureCalendarModule<T>(
  variant: "new" | "legacy",
  load: () => Promise<T>
): Promise<T> {
  const entry = active;
  const started = performance.now();
  try {
    const module = await load();
    loadedModules.add(variant);
    if (entry && active === entry) {
      entry.timings[`${variant}_module_load_ms`] =
        Math.round((performance.now() - started) * 10) / 10;
    }
    return module;
  } catch (error) {
    if (entry) finish(entry, "error", { failure_stage: "route_module" });
    throw error;
  }
}

/** Two animation frames approximate a paint opportunity after the settled commit. */
export function scheduleCalendarEntryReady(
  hasError: boolean,
  details: Measurement
) {
  const entry = active;
  if (!entry || entry.variant !== "new") return () => {};
  markCalendarEntryPhase("grid_settled_ms");
  let frame = requestAnimationFrame(() => {
    frame = requestAnimationFrame(() => {
      if (document.visibilityState !== "visible") {
        finish(entry, "hidden");
        return;
      }
      finish(entry, hasError ? "error" : "ready", details);
    });
  });
  return () => {
    cancelAnimationFrame(frame);
    if (active === entry) delete entry.timings.grid_settled_ms;
  };
}
